/**
 * errors.js — ERROR_UI maps every server + device error code to a UI state,
 * following the thesis "Rejections" matrix. Each entry:
 *   { title_uz, hint_uz, recover }
 *   recover ∈ 'retry' | 'settings' | 'manager' | 'home'
 *     retry    — re-run the same step (e.g. re-check GPS, re-capture face)
 *     settings — open the OS/Telegram permission settings
 *     manager  — escalate to a manager (re-enrol / unlock)
 *     home     — return to Landing (the state resolved itself there)
 *
 * Uzbek-Latin copy. Hours/times are never in these strings — they're states.
 * FACE fail codes (LIVENESS_FAIL / FACE_MISMATCH) escalate to `manager` only
 * AFTER 3 attempts; the screen tracks the attempt count and can override
 * `recover` to 'manager' — the base entry stays 'retry'.
 */

export const ERROR_UI = {
  // ---- auth / pairing -------------------------------------------------------
  AUTH_FAILED: {
    title_uz: 'Tasdiqlash amalga oshmadi',
    hint_uz: 'Ilovani Telegram orqali qayta oching.',
    recover: 'home',
  },
  NOT_PAIRED: {
    title_uz: 'Hisob ulanmagan',
    hint_uz: 'PIN kodingizni kiriting va hisobingizni ulang.',
    recover: 'home',
  },
  ALREADY_PAIRED: {
    title_uz: 'PIN band',
    hint_uz: 'Bu PIN boshqa hisobga ulangan. Menejerga murojaat qiling.',
    recover: 'manager',
  },
  MISSING_PIN: {
    title_uz: 'PIN kiritilmadi',
    hint_uz: 'Davom etish uchun PIN kodni kiriting.',
    recover: 'retry',
  },
  INVALID_PIN: {
    title_uz: 'PIN xato',
    hint_uz: 'Kiritilgan PIN noto‘g‘ri. Qayta urinib ko‘ring.',
    recover: 'retry',
  },
  TOO_MANY_ATTEMPTS: {
    title_uz: 'Juda ko‘p urinish',
    hint_uz: 'Biroz kuting va keyinroq qayta urinib ko‘ring.',
    recover: 'home',
  },

  // ---- consent / enrolment --------------------------------------------------
  NO_CONSENT: {
    title_uz: 'Rozilik kerak',
    hint_uz: 'Avval yuz orqali tasdiqlashga rozilik bering.',
    recover: 'home',
  },
  NOT_ENROLLED: {
    title_uz: 'Yuz ro‘yxatdan o‘tmagan',
    hint_uz: 'Avval yuzingizni ro‘yxatdan o‘tkazing.',
    recover: 'home',
  },
  MISSING_IMAGE: {
    title_uz: 'Rasm olinmadi',
    hint_uz: 'Yuzingiz rasmga tushmadi. Qayta urinib ko‘ring.',
    recover: 'retry',
  },
  NO_FACE: {
    title_uz: 'Yuz aniqlanmadi',
    hint_uz: 'Yorug‘ joyda, yuzingizni to‘liq ko‘rsatib qayta urinib ko‘ring.',
    recover: 'retry',
  },

  // ---- face / liveness (escalate to manager after 3 fails) ------------------
  FACE_MISMATCH: {
    title_uz: 'Yuz mos kelmadi',
    hint_uz: 'Qayta urinib ko‘ring. 3 martadan so‘ng menejerga murojaat qiling.',
    recover: 'retry',
  },
  LIVENESS_FAIL: {
    title_uz: 'Tiriklik tekshiruvidan o‘tolmadingiz',
    hint_uz: 'Yangi topshiriq bo‘yicha qayta urinib ko‘ring. 3 martadan so‘ng menejerga.',
    recover: 'retry',
  },
  FACE_SERVICE_DOWN: {
    title_uz: 'Xizmat vaqtincha ishlamayapti',
    hint_uz: 'Yuzni tekshirish xizmati javob bermadi. Birozdan so‘ng qayta urinib ko‘ring.',
    recover: 'retry',
  },
  CUE_MISSING: {
    title_uz: 'Topshiriq topilmadi',
    hint_uz: 'Tekshiruvni boshidan qayta boshlang.',
    recover: 'retry',
  },
  CUE_EXPIRED: {
    title_uz: 'Topshiriq muddati tugadi',
    hint_uz: 'Juda uzoq kutdingiz. Yangi topshiriq bilan qayta urinib ko‘ring.',
    recover: 'retry',
  },

  // ---- browser-capture handoff token (/capture) -----------------------------
  TOKEN_MISSING: {
    title_uz: 'Havola yaroqsiz',
    hint_uz: 'Telegramga qaytib, «Kirish» yoki «Chiqish» tugmasini qayta bosing.',
    recover: 'home',
  },
  TOKEN_INVALID: {
    title_uz: 'Havola yaroqsiz',
    hint_uz: 'Telegramga qaytib, «Kirish» yoki «Chiqish» tugmasini qayta bosing.',
    recover: 'home',
  },
  TOKEN_EXPIRED: {
    title_uz: 'Havola muddati tugadi',
    hint_uz: 'Juda uzoq kutdingiz. Telegramga qaytib qaytadan boshlang.',
    recover: 'home',
  },
  TOKEN_USED: {
    title_uz: 'Havola allaqachon ishlatilgan',
    hint_uz: 'Bu havola bir marta ishlaydi. Telegramga qaytib qayta urinib ko‘ring.',
    recover: 'home',
  },

  // ---- state machine --------------------------------------------------------
  ALREADY_IN: {
    title_uz: 'Siz allaqachon kirgansiz',
    hint_uz: 'Endi faqat chiqish mumkin.',
    recover: 'home',
  },
  ALREADY_DONE: {
    title_uz: 'Bugungi ish yakunlangan',
    hint_uz: 'Bugun kirish va chiqish bajarilgan.',
    recover: 'home',
  },
  NOT_CHECKED_IN: {
    title_uz: 'Avval kirishingiz kerak',
    hint_uz: 'Chiqishdan oldin kirishni bajaring.',
    recover: 'home',
  },
  DUPLICATE: {
    title_uz: 'Takroriy amal',
    hint_uz: 'Bu amal allaqachon bajarilgan. Bosh sahifaga qayting.',
    recover: 'home',
  },

  // ---- geofence -------------------------------------------------------------
  OUT_OF_RANGE: {
    title_uz: 'Filialdan uzoqdasiz',
    hint_uz: 'Kirish uchun filial radiusi ichida bo‘lishingiz kerak.',
    recover: 'retry',
  },
  NO_BRANCH: {
    title_uz: 'Filial biriktirilmagan',
    hint_uz: 'Sizga filial biriktirilmagan. Menejerga murojaat qiling.',
    recover: 'manager',
  },
  BRANCH_NOT_ALLOWED: {
    title_uz: 'Filial ruxsat etilmagan',
    hint_uz: 'Bu filialda kirish huquqingiz yo‘q. Boshqa filialni tanlang.',
    recover: 'retry',
  },
  BRANCH_NO_COORDS: {
    title_uz: 'Filial joylashuvi sozlanmagan',
    hint_uz: 'Filial koordinatalari yo‘q. Menejerga murojaat qiling.',
    recover: 'manager',
  },
  NO_LOCATION: {
    title_uz: 'Joylashuv aniqlanmadi',
    hint_uz: 'GPS signalini oling va qayta urinib ko‘ring.',
    recover: 'retry',
  },
  ACCURACY_MISSING: {
    title_uz: 'Aniqlik ma’lumoti yo‘q',
    hint_uz: 'Joylashuv aniqligi aniqlanmadi. Qayta urinib ko‘ring.',
    recover: 'retry',
  },
  ACCURACY_TOO_LOW: {
    title_uz: 'Joylashuv aniqligi past',
    hint_uz: 'Ochiq joyga chiqing va GPS aniqlashini kuting.',
    recover: 'retry',
  },

  // ---- device / location manager (client-side codes) ------------------------
  DESKTOP: {
    title_uz: 'Telefondan foydalaning',
    hint_uz: 'Joylashuv faqat telefondagi Telegram ilovasida ishlaydi.',
    recover: 'home',
  },
  DENIED: {
    title_uz: 'Joylashuvga ruxsat berilmadi',
    hint_uz: 'Sozlamalardan joylashuvga ruxsat bering.',
    recover: 'settings',
  },
  UNAVAILABLE: {
    title_uz: 'GPS o‘chirilgan yoki aniqlanmadi',
    hint_uz: 'GPS ni yoqing va qayta urinib ko‘ring.',
    recover: 'retry',
  },
  CAM_DENIED: {
    title_uz: 'Kameraga ruxsat berilmadi',
    hint_uz: 'Sozlamalardan kameraga ruxsat bering.',
    recover: 'settings',
  },
  NO_CAM: {
    title_uz: 'Kamera topilmadi',
    hint_uz: 'Qurilmada kamera aniqlanmadi. Qayta urinib ko‘ring.',
    recover: 'retry',
  },

  // ---- generic / transport --------------------------------------------------
  NETWORK: {
    title_uz: 'Internet bilan bog‘lanmadi',
    hint_uz: 'Internet aloqasini tekshirib, qayta urinib ko‘ring.',
    recover: 'retry',
  },
  SERVER_ERROR: {
    title_uz: 'Server xatosi',
    hint_uz: 'Nimadir noto‘g‘ri ketdi. Birozdan so‘ng qayta urinib ko‘ring.',
    recover: 'retry',
  },
  BAD_RANGE: {
    title_uz: 'Noto‘g‘ri oy',
    hint_uz: 'Tanlangan oy mavjud emas. Boshqa oyni tanlang.',
    recover: 'home',
  },
};

/** Fallback for an unknown code — never leave the worker on a blank screen. */
export const UNKNOWN_ERROR = {
  title_uz: 'Xatolik yuz berdi',
  hint_uz: 'Qayta urinib ko‘ring.',
  recover: 'retry',
};

/** Resolve an error code (any string) to its UI descriptor. */
export function errorUI(code) {
  return ERROR_UI[code] || UNKNOWN_ERROR;
}

export default ERROR_UI;
